import { getSettings, listAdminUsers, listProfiles } from "@/lib/api/endpoints";
import { useCurrentUser, type SessionUser } from "@/lib/auth/session";
import { PageLoading, useLoader } from "@/lib/loader";
import { localClaudeCapability } from "@/modules/ai/providers";
import {
  SettingsForm,
  type AccessData,
  type AccessUserRow,
  type LoadedSettings,
} from "./SettingsForm";

async function loadSettings(): Promise<LoadedSettings> {
  try {
    const [data, profiles] = await Promise.all([
      getSettings(),
      listProfiles(),
    ]);
    return {
      settings: data.settings,
      masked: data.masked,
      templateCount: profiles.length,
      localClaude: localClaudeCapability(data),
    };
  } catch {
    return { settings: {} };
  }
}

async function loadAccess(user: SessionUser): Promise<AccessData> {
  if (user.role !== "SUPER_ADMIN") {
    return { isSuperAdmin: false };
  }
  let users: AccessUserRow[] = [];
  try {
    users = await listAdminUsers();
  } catch {
    users = [];
  }
  return {
    isSuperAdmin: true,
    currentEmail: user.email.toLowerCase(),
    envSuperAdmins: users.filter((u) => u.isBootstrapAdmin).map((u) => u.email).sort(),
    users,
  };
}

export function SettingsPage() {
  const user = useCurrentUser();
  const loaded = useLoader(() => Promise.all([loadSettings(), loadAccess(user)]), [user]);
  if (!loaded.data) return <PageLoading />;
  const [data, access] = loaded.data;

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 lg:px-6">
      <div className="mb-8">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
          Configuration
        </p>
        <h1 className="font-sans text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-100">
          Settings
        </h1>
        <p className="mt-1 text-[13px] text-zinc-600 dark:text-zinc-400">
          Appearance, integrations, templates, and access, grouped by tab.
        </p>
      </div>

      <SettingsForm initial={data} access={access} />
    </div>
  );
}
